import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { useNotificationContext } from '../contexts/NotificationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const StudentManagement = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const [filters, setFilters] = useState({
    department: '',
    academicYear: '',
    class: ''
  });

  const [formData, setFormData] = useState({
    nationalIdNumber: '',
    studentCardNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    dateOfBirth: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (filters.department && filters.academicYear) {
      fetchClasses();
    }
  }, [filters.department, filters.academicYear]);

  useEffect(() => {
    if (filters.class && filters.academicYear) {
      fetchStudents();
    }
  }, [filters.class, filters.academicYear]);

  const fetchInitialData = async () => {
    try {
      const [deptRes, yearRes] = await Promise.all([
        axios.get(`${API_URL}/api/departments`),
        axios.get(`${API_URL}/api/academic-years`)
      ]);
      setDepartments(deptRes.data);
      setAcademicYears(yearRes.data);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      showError('Erreur lors du chargement des données');
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/classes`);
      const filtered = response.data.filter(
        cls => cls.department._id === filters.department && 
               cls.academicYear._id === filters.academicYear
      );
      setClasses(filtered);
    } catch (error) {
      console.error('Erreur chargement classes:', error);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/students?class=${filters.class}&academicYear=${filters.academicYear}&_t=${Date.now()}`;
      console.log('🔍 Fetching students:', url);
      console.log('🔍 Filters:', filters);
      
      const response = await axios.get(url);
      console.log('✅ Students received:', response.data.length, response.data);
      
      setStudents(response.data);
    } catch (error) {
      console.error('❌ Erreur chargement étudiants:', error);
      console.error('❌ Response:', error.response?.data);
      showError('Erreur lors du chargement des étudiants');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    if (name === 'department' || name === 'academicYear') {
      setFilters(prev => ({ ...prev, class: '' }));
      setStudents([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openModal = (student = null) => {
    if (student) {
      setEditingStudent(student);
      setFormData({
        nationalIdNumber: student.nationalIdNumber,
        studentCardNumber: student.studentCardNumber || '',
        firstName: student.firstName,
        lastName: student.lastName,
        email: student.email || '',
        phoneNumber: student.phoneNumber || '',
        address: student.address || '',
        dateOfBirth: student.dateOfBirth ? student.dateOfBirth.split('T')[0] : ''
      });
    } else {
      setEditingStudent(null);
      setFormData({
        nationalIdNumber: '',
        studentCardNumber: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        address: '',
        dateOfBirth: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStudent(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await axios.put(`${API_URL}/api/students/${editingStudent._id}`, formData);
        showSuccess('Étudiant modifié avec succès');
      } else {
        const newStudent = await axios.post(`${API_URL}/api/students`, formData);
        await axios.post(`${API_URL}/api/students/${newStudent.data._id}/enroll`, {
          classId: filters.class,
          academicYearId: filters.academicYear
        });
        showSuccess('Étudiant ajouté avec succès');
      }
      closeModal();
      fetchStudents();
    } catch (error) {
      console.error('Erreur:', error);
      showError(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet étudiant ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/students/${studentId}`);
      showSuccess('Étudiant supprimé avec succès');
      fetchStudents();
    } catch (error) {
      console.error('Erreur suppression:', error);
      showError('Erreur lors de la suppression');
    }
  };

  const selectedClass = classes.find(c => c._id === filters.class);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">👥 Gestion des Étudiants</h1>
          <p className="text-gray-600">Gérer les étudiants par classe et année universitaire</p>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
              <select
                name="department"
                value={filters.department}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
              <select
                name="academicYear"
                value={filters.academicYear}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
              <select
                name="class"
                value={filters.class}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner une classe</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Liste des étudiants */}
        {filters.class && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedClass?.name} - {students.length} étudiant(s)
                </h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowImportModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  📄 Importer Excel
                </button>
                <button
                  onClick={() => openModal()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                >
                  ➕ Ajouter un étudiant
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : students.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">📚</div>
                <p>Aucun étudiant dans cette classe</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CIN</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carte étudiant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom complet</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {students.map(student => (
                      <tr key={student._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{student.nationalIdNumber}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{student.studentCardNumber || '-'}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {student.firstName} {student.lastName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{student.email || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{student.phoneNumber || '-'}</td>
                        <td className="px-6 py-4 text-right text-sm">
                          <button
                            onClick={() => openModal(student)}
                            className="text-primary-600 hover:text-primary-900 mr-3"
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(student._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            🗑️ Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Modal Import */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Importer des étudiants depuis Excel</h3>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier Excel
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: N° | CIN | Nom | Prénom
                  </p>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <strong>Note:</strong> Les étudiants existants seront mis à jour, les nouveaux seront créés.
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={importing}
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    if (!importFile) {
                      showError('Veuillez sélectionner un fichier');
                      return;
                    }
                    if (!filters.class || !filters.academicYear) {
                      showError('Veuillez sélectionner une classe et une année universitaire');
                      return;
                    }

                    setImporting(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', importFile);
                      formData.append('classId', filters.class);
                      formData.append('academicYearId', filters.academicYear);

                      const response = await axios.post(`${API_URL}/api/students/import`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });

                      showSuccess(`Import réussi: ${response.data.results.created} créés, ${response.data.results.updated} mis à jour`);
                      if (response.data.results.errors.length > 0) {
                        console.warn('Erreurs d\'import:', response.data.results.errors);
                      }
                      setShowImportModal(false);
                      setImportFile(null);
                      fetchStudents();
                    } catch (error) {
                      console.error('Erreur import:', error);
                      showError(error.response?.data?.message || 'Erreur lors de l\'import');
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!importFile || importing}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {importing ? 'Import en cours...' : 'Importer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold">
                  {editingStudent ? 'Modifier l\'étudiant' : 'Ajouter un étudiant'}
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CIN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nationalIdNumber"
                      value={formData.nationalIdNumber}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Carte étudiant</label>
                    <input
                      type="text"
                      name="studentCardNumber"
                      value={formData.studentCardNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                  >
                    {editingStudent ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentManagement;
